import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';

export default function PrivacyPolicyPage() {
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen pb-20 transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-300' : 'bg-white text-gray-700'}`}>
      <SEO
        {...pageSeoMeta.policyPrivacy}
        jsonLd={buildBreadcrumbJsonLd([
          { name: 'Home', url: '/' },
          { name: 'Privacy Policy', url: '/policies/privacy' },
        ])}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Privacy Policy' },
      ]} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 mb-8 text-sm font-medium">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className={`text-3xl sm:text-4xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Privacy Policy</h1>
          <p className={`text-sm mb-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Last updated: March 2026</p>

          <div className="space-y-8 leading-relaxed">
            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Information We Collect</h2>
              <p>When you use ZeroSmoke, we may collect the following information:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Personal Information:</strong> Name, email address, phone number, and shipping address provided during account creation or checkout.</li>
                <li><strong>Order Data:</strong> Products purchased, order history, and payment method (we do not store card details).</li>
                <li><strong>Usage Data:</strong> Pages visited, browser type, device information, and IP address for analytics purposes.</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Process and fulfill your orders.</li>
                <li>Send order confirmations, shipping updates, and delivery notifications.</li>
                <li>Improve our website, products, and customer experience.</li>
                <li>Communicate promotional offers (only with your consent — you can opt out anytime).</li>
                <li>Prevent fraud and ensure platform security.</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Data Sharing</h2>
              <p>We do <strong>not sell</strong> your personal data to third parties. We may share data with:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Shipping partners</strong> — to deliver your orders.</li>
                <li><strong>Payment gateways</strong> — to process transactions securely.</li>
                <li><strong>Analytics tools</strong> — to understand website usage (anonymized data).</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Cookies</h2>
              <p>We use cookies to improve your browsing experience, remember your preferences, and analyze site traffic. You can disable cookies in your browser settings, though this may affect website functionality.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Data Security</h2>
              <p>We implement industry-standard security measures including SSL encryption, secure payment processing, and regular security audits to protect your information.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Your Rights</h2>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Correction:</strong> Request corrections to inaccurate data.</li>
                <li><strong>Deletion:</strong> Request deletion of your account and associated data.</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time.</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Changes to This Policy</h2>
              <p>We may update this Privacy Policy periodically. Any changes will be posted on this page. We encourage you to review this page regularly.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Contact Us</h2>
              <p>For privacy-related concerns, contact us at <a href="mailto:zerosmoke2026@gmail.com" className="text-emerald-500 hover:underline">zerosmoke2026@gmail.com</a>.</p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
